import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

axios.defaults.baseURL = API_BASE;

// Request interceptor
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login failures - let the login page handle the error
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login');
    
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  login: (email, password) => axios.post('/auth/login', { email, password }),
  register: (userData) => axios.post('/auth/register', userData),
  getCurrentUser: () => axios.get('/auth/me'),

  // Dashboard
  getDashboardStats: (filters = {}) => axios.get('/dashboard/stats', { params: filters }),
  getRecentActivity: (limit = 10) => axios.get('/dashboard/recent-activity', { params: { limit } }),
  getPendingItems: () => axios.get('/dashboard/pending-items'),

  // Customers
  getCustomers: (search) => axios.get('/customers', { params: { search } }),
  getCustomer: (id) => axios.get(`/customers/${id}`),
  createCustomer: (data) => axios.post('/customers', data),
  updateCustomer: (id, data) => axios.put(`/customers/${id}`, data),
  
  // Customer Contacts
  getCustomerContacts: (customerId) => axios.get(`/customers/${customerId}/contacts`),
  createCustomerContact: (customerId, data) => axios.post(`/customers/${customerId}/contacts`, data),
  updateCustomerContact: (customerId, contactId, data) => axios.put(`/customers/${customerId}/contacts/${contactId}`, data),
  deleteCustomerContact: (customerId, contactId) => axios.delete(`/customers/${customerId}/contacts/${contactId}`),
  assignContactToSite: (customerId, contactId, siteId) => axios.post(`/customers/${customerId}/contacts/${contactId}/assign-site/${siteId}`),
  
  // Sites
  getCustomerSites: (customerId) => axios.get(`/customers/${customerId}/sites`),
  getSite: (id) => axios.get(`/sites/${id}`),
  createSite: (customerId, data) => axios.post(`/customers/${customerId}/sites`, data),
  updateSite: (siteId, data) => axios.put(`/sites/${siteId}`, data),
  deleteSite: (siteId) => axios.delete(`/sites/${siteId}`),
  
  // Items
  getItems: (activeOnly = true) => axios.get('/items', { params: { active_only: activeOnly } }),
  createItem: (data) => axios.post('/items', data),
  updateItem: (id, data) => axios.put(`/items/${id}`, data),
  deleteItem: (id) => axios.delete(`/items/${id}`),
  
  // Pricing
  getCustomerPrices: (customerId) => axios.get(`/customers/${customerId}/prices`),
  createCustomerPrice: (customerId, data) => axios.post(`/customers/${customerId}/prices`, data),
  deleteCustomerPrice: (customerId, priceId) => axios.delete(`/customers/${customerId}/prices/${priceId}`),
  getSitePrices: (siteId) => axios.get(`/sites/${siteId}/prices`),
  createSitePrice: (siteId, data) => axios.post(`/sites/${siteId}/prices`, data),
  deleteSitePrice: (siteId, priceId) => axios.delete(`/sites/${siteId}/prices/${priceId}`),
  
  // Work Orders
  getWorkOrders: (params) => axios.get('/work-orders', { params }),
  getWorkOrder: (id) => axios.get(`/work-orders/${id}`),
  createWorkOrder: (data) => axios.post('/work-orders', data),
  updateWorkOrder: (id, data) => axios.put(`/work-orders/${id}`, data),
  addWorkOrderLine: (workOrderId, data) => axios.post(`/work-orders/${workOrderId}/lines`, data),
  updateWorkOrderLine: (workOrderId, lineId, data) => axios.put(`/work-orders/${workOrderId}/lines/${lineId}`, data),
  deleteWorkOrderLine: (workOrderId, lineId) => axios.delete(`/work-orders/${workOrderId}/lines/${lineId}`),
  promoteWorkOrderLineToCatalog: (workOrderId, lineId, data) => axios.post(`/work-orders/${workOrderId}/lines/${lineId}/promote-to-catalog`, data),
  updateLineDiscount: (workOrderId, lineId, discountPercent) => axios.put(`/work-orders/${workOrderId}/lines/${lineId}/discount`, { discount_percent: discountPercent }),
  submitWorkOrder: (id) => axios.post(`/work-orders/${id}/submit`),
  approveWorkOrder: (id) => axios.post(`/work-orders/${id}/approve`),
  rejectWorkOrder: (id, comment) => axios.post(`/work-orders/${id}/reject`, { comment }),
  reopenWorkOrder: (id) => axios.post(`/work-orders/${id}/reopen`),
  cancelWorkOrder: (id) => axios.post(`/work-orders/${id}/cancel`),
  deleteWorkOrder: (id) => axios.delete(`/work-orders/${id}`),
  downloadWorkOrderPDF: (id, version = 'revised') => axios.get(`/work-orders/${id}/pdf`, { params: { version }, responseType: 'blob' }),
  getWorkOrderAudit: (id) => axios.get(`/work-orders/${id}/audit`),
  getWorkOrderOriginal: (id) => axios.get(`/work-orders/${id}/original`),
  sendWorkOrderEmail: (id, data) => axios.post(`/work-orders/${id}/send-email`, data),
  saveWorkOrderSignature: (id, data) => axios.post(`/work-orders/${id}/signature`, data),
  
  // Invoices
  getInvoices: (params) => axios.get('/invoices', { params }),
  getInvoice: (id) => axios.get(`/invoices/${id}`),
  generateInvoice: (workOrderIds) => axios.post('/invoices/generate', { work_order_ids: workOrderIds }),
  downloadInvoicePDF: (id) => axios.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  issueInvoice: (id) => axios.post(`/invoices/${id}/issue`),
  sendInvoice: (id, recipients) => axios.post(`/invoices/${id}/send`, { recipients }),
  markInvoiceSent: (id) => axios.post(`/invoices/${id}/mark-sent`),
  settleInvoice: (id) => axios.post(`/invoices/${id}/settle`),
  cancelInvoice: (id) => axios.post(`/invoices/${id}/cancel`),
  sendInvoiceEmail: (id) => axios.post(`/invoices/${id}/send-email`),
  markInvoiceMailSent: (id) => axios.post(`/invoices/${id}/mark-mail-sent`),
  registerPayment: (invoiceId, data) => axios.post(`/invoices/${invoiceId}/payments`, data),
  getInvoicePayments: (invoiceId) => axios.get(`/invoices/${invoiceId}/payments`),
  updateInvoiceOverrides: (invoiceId, overrides) => axios.patch(`/invoices/${invoiceId}/overrides`, overrides),
  addInvoiceLine: (invoiceId, data) => axios.post(`/invoices/${invoiceId}/lines`, data),
  updateInvoiceLine: (invoiceId, lineId, data) => axios.patch(`/invoices/${invoiceId}/lines/${lineId}`, data),
  deleteInvoiceLine: (invoiceId, lineId) => axios.delete(`/invoices/${invoiceId}/lines/${lineId}`),
  reorderInvoiceLines: (invoiceId, lineOrders) => axios.post(`/invoices/${invoiceId}/lines/reorder`, lineOrders),
  
  // Activity Logs
  getActivityLogs: (params) => axios.get('/activity-logs', { params }),
  
  // Users
  getUsers: () => axios.get('/users'),
  getSelectableEmployees: () => axios.get('/users/selectable-employees'),
  updateUser: (id, data) => axios.put(`/users/${id}`, data),
  resetUserPassword: (id, newPassword) => axios.post(`/users/${id}/reset-password`, { new_password: newPassword }),
  deleteUser: (id) => axios.delete(`/users/${id}`),
  
  // Company Settings
  getCompanySettings: () => axios.get('/settings/company'),
  updateCompanySettings: (data) => axios.post('/settings/company', data),
  uploadCompanyLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post('/settings/company/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteCompanyLogo: () => axios.delete('/settings/company/logo'),
  
  // Units
  getUnits: (activeOnly = false) => axios.get('/settings/units', { params: { active_only: activeOnly } }),
  createUnit: (data) => axios.post('/settings/units', data),
  updateUnit: (id, data) => axios.put(`/settings/units/${id}`, data),
  deleteUnit: (id) => axios.delete(`/settings/units/${id}`),
  
  // Timesheets
  getTimesheets: (params) => axios.get('/timesheets', { params }),
  createTimesheet: (data) => axios.post('/timesheets', data),
  updateTimesheet: (id, data) => axios.put(`/timesheets/${id}`, data),
  submitTimesheet: (id) => axios.post(`/timesheets/${id}/submit`),
  approveTimesheet: (id) => axios.post(`/timesheets/${id}/approve`),
  deleteTimesheet: (id) => axios.delete(`/timesheets/${id}`),
  
  // Absences
  getAbsences: (params) => axios.get('/absences', { params }),
  createAbsence: (data) => axios.post('/absences', data),
  updateAbsence: (id, data) => axios.put(`/absences/${id}`, data),
  approveAbsence: (id) => axios.post(`/absences/${id}/approve`),
  rejectAbsence: (id) => axios.post(`/absences/${id}/reject`),
  deleteAbsence: (id) => axios.delete(`/absences/${id}`),
};

export default api;
